import { type GrandTotalsData } from 'common/visualizations/views/agGridReact/types';
import { type TableColumn } from '../types';
import { type Row } from './type';
import { generateLookupKey } from 'common/visualizations/helpers/TableDataExportTotalHelpers';

/**
 * Post-processor that replaces client-side aggregated values in grouped PDF export rows
 * with server-side computed values from Advanced Totaling custom formulas.
 *
 * **why this exists:**
 *
 * the PDF export pipeline uses client-side grouping (`gridDataGrouper`) which computes
 * subtotals/totals via simple sum/avg/min/max/count. this breaks for custom formulas
 * (e.g. `` `US` + `CA` * 2 ``) because the formula logic only exists server-side in SoQL.
 * this function patches the client-side results with the correct server-computed values.
 *
 * **input:**
 * - `rows`: the output of `groupRows()` -- a flat array of header, subtotal, normal, and total rows
 *   with client-side aggregated values already computed
 * - `grandTotals`: server-side computed grand total values keyed by column field name
 *   (e.g. `{ sales: 5000, profit: 1200 }`)
 * - `subtotalLookup`: O(1) lookup map from `generateLookupKey(level, groupKeys, column)` → value
 *   (e.g. `"0:USA:sales" → 2500`, `"1:USA|NY:profit" → 300`)
 * - `levelMetadata`: maps each group level to its group column names and value column names
 *
 * **process:**
 * 1. identifies value columns (columns with an `aggFunc`) from `tableColumns`
 * 2. for each row, checks `_rowType`:
 *    - `'total'` → replaces values from `grandTotals`
 *    - `'header'` or `'subTotal'` → extracts group keys from the row using `levelMetadata`,
 *      builds a lookup key via `generateLookupKey`, and replaces values from `subtotalLookup`
 *    - normal rows → left unchanged
 *
 * **output:**
 * - same row array with aggregated values on header/subtotal/total rows replaced by server values
 * - rows with no matching lookup entry are returned unchanged
 *
 * @example
 * // =========================================================================
 * // SCENARIO: two-level grouping (country → state) with custom formula
 * // =========================================================================
 * // formula on country level: "`US` + `CA`"
 * //
 * // input rows (after groupRows):
 * //   { country: 'US', sales: 300, _rowType: 'header', _groupLevel: 0 }   ← client-side sum
 * //   { country: 'US', state: 'NY', sales: 100 }                          ← normal row
 * //   { country: 'US', state: 'CA', sales: 200 }                          ← normal row
 * //   { _rowType: 'total', sales: 300 }                                   ← client-side sum
 * //
 * // subtotalLookup: Map { "0:US:sales" → 250 }  (server computed: formula result)
 * // grandTotals: { sales: 450 }                  (server computed: formula result)
 * //
 * // output rows:
 * //   { country: 'US', sales: 250, _rowType: 'header', _groupLevel: 0 }   ← replaced
 * //   { country: 'US', state: 'NY', sales: 100 }                          ← unchanged
 * //   { country: 'US', state: 'CA', sales: 200 }                          ← unchanged
 * //   { _rowType: 'total', sales: 450 }                                   ← replaced
 */
export function replaceWithServerTotals(
  rows: Row[],
  tableColumns: TableColumn[],
  grandTotals: GrandTotalsData | Array<GrandTotalsData> | null | undefined,
  subtotalLookup: Map<string, unknown> | undefined,
  levelMetadata: Map<number, { groupColumns: string[]; valueColumns: string[] }> | undefined,
): Row[] {
  const hasSubtotals = subtotalLookup && subtotalLookup.size > 0 && levelMetadata;
  if (!hasSubtotals && !grandTotals) {
    return rows;
  }

  const valueColumnFields = tableColumns.filter((col) => col.aggFunc && col.field).map((col) => col.field);

  return rows.map((row) => {
    if (row._rowType === 'total' && grandTotals) {
      return replaceGrandTotalValues(row, grandTotals, valueColumnFields);
    }

    if ((row._rowType === 'header' || row._rowType === 'subTotal') && hasSubtotals) {
      return replaceSubtotalValues(row, subtotalLookup, levelMetadata, valueColumnFields);
    }

    return row;
  });
}

/**
 * replaces aggregated values on a grand total row with server-computed values.
 *
 * **input:** a total row with client-side aggregated values, and server grand totals keyed by field name
 * **process:** for each value column, if the field exists in `grandTotals`, overwrites the row's value
 * **output:** a shallow copy of the row with replaced values (partial replacement is supported --
 *   fields missing from `grandTotals` retain their client-side values)
 */
function replaceGrandTotalValues(
  row: Row,
  grandTotals: GrandTotalsData | Array<GrandTotalsData>,
  valueColumnFields: string[],
): Row {
  const updatedRow = { ...row };
  // TODO EN-92427 [export-pivot-return-type]: Pivot tables use processPivot() (GridDataProcessor line 40) and bypass this code,
  // so grandTotals should always be an object here. We handle arrays defensively for type safety.
  const totalsObj = Array.isArray(grandTotals) ? grandTotals[0] : grandTotals;
  valueColumnFields.forEach((field) => {
    if (field in totalsObj) {
      updatedRow[field] = totalsObj[field];
    }
  });
  return updatedRow;
}

/**
 * replaces aggregated values on a header or subtotal row with server-computed values
 * by looking up the row's group key combination in the subtotal lookup map.
 *
 * **input:**
 * - a header/subtotal row with `_groupLevel: N` and group column values (e.g. `{ country: 'US', _groupLevel: 0 }`)
 * - `subtotalLookup`: map from `"level:key1|key2:column"` → server-computed value
 * - `levelMetadata`: maps level N to its group columns (used to extract the correct keys from the row)
 *
 * **process:**
 * 1. reads `_groupLevel` from the row to determine which hierarchy level we're at
 * 2. gets the group column names for that level from `levelMetadata`
 * 3. extracts group key values from the row (null values are encoded as the string `"null"`)
 * 4. for each value column, builds a lookup key and checks the map
 *
 * **output:** a shallow copy with replaced values, or the original row reference if no matches found
 *
 * @example
 * // row: { country: 'US', state: 'NY', sales: 100, _rowType: 'subTotal', _groupLevel: 1 }
 * // levelMetadata: Map { 1 → { groupColumns: ['country', 'state'], ... } }
 * // lookup key built: "1:US|NY:sales"
 * // if subtotalLookup.has("1:US|NY:sales") → replaces sales with the server value
 */
function replaceSubtotalValues(
  row: Row,
  subtotalLookup: Map<string, unknown>,
  levelMetadata: Map<number, { groupColumns: string[]; valueColumns: string[] }>,
  valueColumnFields: string[],
): Row {
  const level = row._groupLevel;
  if (typeof level !== 'number') {
    return row;
  }

  const metadata = levelMetadata.get(level);
  if (!metadata) {
    return row;
  }

  const groupKeys = metadata.groupColumns.map((col) => {
    const value = row[col];
    return typeof value === 'string' ? value : String(value ?? 'null');
  });

  const updatedRow = { ...row };
  let hasReplacement = false;

  valueColumnFields.forEach((field) => {
    const key = generateLookupKey(level, groupKeys, field);
    if (subtotalLookup.has(key)) {
      updatedRow[field] = subtotalLookup.get(key);
      hasReplacement = true;
    }
  });

  return hasReplacement ? updatedRow : row;
}
